import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import StructuredData from '@/components/base/StructuredData';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteConfig } from '@/mocks/site';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9q8olnnahb7382ov7t0';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    setFormStatus('loading');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于本站 - ${siteConfig.name}`,
    description: siteConfig.description,
    url: `${siteConfig.url}/about`,
  };

  return (
    <>
      <SEOHead
        title={`关于本站 - ${siteConfig.name}`}
        description={`了解${siteConfig.name}的品牌故事、信誉保障与联系方式。最全面的冲田杏梨中文资料库。`}
        keywords={`关于本站,冲田杏梨粉丝站,${siteConfig.keywords}`}
        canonicalUrl={`${siteConfig.url}/about`}
      />
      <StructuredData data={orgSchema} />
      <StructuredData data={aboutSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于本站' },
            ]}
          />

          {/* Brand Introduction */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-6 font-[family-name:var(--font-heading)]">
                关于本站
              </h1>
              <div className="prose prose-sm md:prose-base max-w-none">
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  <strong>冲田杏梨粉丝资讯站</strong>是一个由粉丝创建和维护的非官方中文资讯平台，致力于为华语地区的冲田杏梨粉丝提供最全面、最及时的一站式信息参考。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  本站汇集了冲田杏梨的完整个人资料、出道至今的职业经历、所有写真集和影视作品的收录、最新新闻动态以及音乐作品信息。我们的编辑团队持续关注冲田杏梨的公开动态，确保内容始终保持更新。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  作为粉丝资讯平台，本站所有内容均来源于公开渠道和官方发布的信息。我们致力于为用户提供准确、客观的信息参考，帮助新老粉丝更好地了解冲田杏梨这位优秀的艺人。
                </p>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-[family-name:var(--font-heading)]">
                信誉保障
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  {
                    icon: 'ri-shield-check-line',
                    title: '信息准确',
                    desc: '所有内容均经过多次核实，确保信息来源可靠，杜绝虚假和误导性信息。',
                  },
                  {
                    icon: 'ri-refresh-line',
                    title: '持续更新',
                    desc: '编辑团队持续关注冲田杏梨最新动态，第一时间更新相关内容。',
                  },
                  {
                    icon: 'ri-user-voice-line',
                    title: '粉丝为本',
                    desc: '以粉丝需求为核心，提供最实用的信息检索和阅读体验。',
                  },
                  {
                    icon: 'ri-eye-off-line',
                    title: '内容尊重',
                    desc: '尊重艺人隐私，只报道公开渠道的资讯，不传播未经证实的消息。',
                  },
                  {
                    icon: 'ri-global-line',
                    title: '中文优先',
                    desc: '为华语粉丝量身定制，提供高质量的简体中文内容。',
                  },
                  {
                    icon: 'ri-heart-line',
                    title: '非盈利',
                    desc: '本站为非盈利性质的粉丝资讯平台，不涉及任何商业交易。',
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="p-5 rounded-lg bg-background-50 border border-background-200/70"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-3">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-2xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-3 font-[family-name:var(--font-heading)]">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 mb-8">
                如果您对本站内容有任何建议、意见或合作意向，欢迎通过以下表单与我们联系。
              </p>

              {formStatus === 'success' ? (
                <div className="p-8 rounded-lg bg-accent-50 border border-accent-200 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2">提交成功！</h3>
                  <p className="text-sm text-foreground-600">感谢您的来信，我们会尽快回复。</p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="mt-5 px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    发送新消息
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-5"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的姓名 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入您的电子邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息内容 <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                      placeholder="请输入您的消息内容（不超过500字）"
                    ></textarea>
                    <p className="mt-1 text-xs text-foreground-400">最多500个字符</p>
                  </div>

                  {/* Honeypot */}
                  <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                      aria-hidden="true"
                    />
                  </div>

                  {formStatus === 'error' && formError && (
                    <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-600">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full sm:w-auto px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中...
                      </span>
                    ) : (
                      '发送消息'
                    )}
                  </button>
                </form>
              )}
            </div>
          </section>

          {/* Disclaimer */}
          <section className="py-10 md:py-12 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-lg font-semibold text-foreground-900 mb-3 font-[family-name:var(--font-heading)]">
                免责声明
              </h2>
              <p className="text-xs text-foreground-600 leading-relaxed">
                {siteConfig.name}为非官方粉丝资讯网站，与冲田杏梨本人及其经纪公司无任何直接关联。本站所有内容均来自公开渠道收集整理，仅供粉丝参考使用。如涉及版权或其他问题，请通过联系表单与我们沟通，我们将及时处理。
              </p>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}